import { z } from 'zod';

import {
  newPasswordSchema,
  PASSWORD_MAX_LENGTH,
  roleSummarySchema,
  timestampSchema,
  userStatusSchema,
} from '@/types/schemas';

/** `GET /auth/me` and the `user` in the login response. */
export const authProfileSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  mobile: z.string().nullable(),
  role: roleSummarySchema,
  permissions: z.array(z.string()),
  status: userStatusSchema,
  mustChangePassword: z.boolean(),
  lastLoginAt: timestampSchema.nullable(),
  /** Present for member logins: whose account this is. */
  member: z
    .object({
      id: z.number(),
      memberCode: z.string(),
      firstName: z.string(),
      lastName: z.string().nullable(),
    })
    .nullable(),
});
export type AuthProfile = z.infer<typeof authProfileSchema>;

export const loginResultSchema = z.object({
  token: z.string().min(1),
  expiresAt: timestampSchema,
  user: authProfileSchema,
});
export type LoginResult = z.infer<typeof loginResultSchema>;

export const loginFormSchema = z.object({
  identifier: z.string().trim().min(1, 'Enter your email or mobile number').max(255),
  password: z.string().min(1, 'Enter your password').max(PASSWORD_MAX_LENGTH),
  remember: z.boolean(),
});
export type LoginFormValues = z.infer<typeof loginFormSchema>;

export const changePasswordFormSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password').max(PASSWORD_MAX_LENGTH),
    newPassword: newPasswordSchema,
    confirmPassword: z.string(),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'The two passwords do not match',
  })
  .refine((values) => values.newPassword !== values.currentPassword, {
    path: ['newPassword'],
    message: 'Choose a password different from the current one',
  });

/** The name shown on the dashboard and in the account menu. */
export const profileNameFormSchema = z.object({
  name: z.string().trim().min(1, 'Enter your name').max(150, 'Keep it under 150 characters'),
});

export interface UpdateProfileInput {
  name: string;
}

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}
